import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { District, PrimaryCategory2, RequestTheoryActualReportRequest, Store } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport, getFormattedDate } from '../../../../utils';
import { UserContext } from '../../../../components/shared/useUser';

type IProps = {
  onClose?: () => void;
}

const ActualVsTheoryAmrestReportForm = (props: IProps) => {
  const { selectedStore, user } = useContext(UserContext);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<District[]>([]);
  const [allCategories, setAllCategories] = useState<PrimaryCategory2[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<PrimaryCategory2[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [availableEndDates, setAvailableEndDates] = useState<string[]>([]);
  const [availableStartDates, setAvailableStartDates] = useState<string[]>([]);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { getPrimaryCategoriesForStore, runTheoryActualReport, getDistrictsByFranchise, getReportAvailableDates } = useNswagClient();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      loadDistricts();
      loadCategories(selectedStore.storeNumber);
    }
  }, [selectedStore]);

  const loadDistricts = () => {
    if (!selectedStore?.franchiseName) return;
    getDistrictsByFranchise(selectedStore.franchiseName).then((res) => {
      if (res.data && Array.isArray(res.data)) {
        setAllDistricts(res.data);
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  const loadCategories = (storeNumber: string) => {
    getPrimaryCategoriesForStore(storeNumber).then((res) => {
      if (res.data) {
        setAllCategories(res.data);
        setSelectedCategories(res.data);
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  const loadAvailableDates = (stores: Store[], districts: District[], selectAllStoresChecked: boolean) => {
    if (stores.length === 0 && districts.length === 0 && !selectAllStoresChecked) {
      setAvailableEndDates([]);
      return;
    }
    getReportAvailableDates(stores.map(store => store.storeNumber).join(','), districts.map(district => district.id).join(','), selectAllStoresChecked, 26)
      .then((res) => {
        if (res.data) {
          setAvailableEndDates(res.data.map(date => new Date(date).toISOString()));
          setAvailableStartDates(res.data.map(date => {
            const newDate = new Date(date);
            newDate.setDate(newDate.getDate() + 1);
            return newDate.toISOString();
          }));
        }
      });
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSelectedStores([]);

    setCheckedAllStores(checked);

    loadAvailableDates([], selectedDistricts, checked);
  };

  const handleSelectAllCategoriesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    if (checked) {
      setSelectedCategories(allCategories);
    } else {
      setSelectedCategories([]);
    }
  };

  const handleDateChange = (event: SyntheticEvent, value: string | null) => {
    setFromDate(value ?? '');
    setToDate('');
  };

  const handleCategorySelectChange = (event: SyntheticEvent, value: PrimaryCategory2[]) => {
    setSelectedCategories(value);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);

    loadAvailableDates(value, [], checkedAllStores);

    if (value.length > 0) {
      setSelectedDistricts([]);
    }
  };

  const handleDistrictChange = (event: SyntheticEvent, value: District[]) => {
    setSelectedDistricts(value);

    if (value.length > 0) {
      setSelectedStores([]);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const from = new Date(new Date(fromDate));
    const to = new Date(new Date(toDate));
    const selectedAllStores = checkedAllStores || (selectedStores.length > 0 && selectedStores.length == user?.stores?.length);

    const request: RequestTheoryActualReportRequest = {
      reportOnAllAvailableStores: selectedAllStores,
      primaryCategoryIds: selectedCategories.length === allCategories.length ? [] : selectedCategories.map(x => x.id as number),
      stores: selectedAllStores ? undefined : selectedStores.map(x => x.storeNumber as string),
      districts: selectedDistricts.length === 0 ? undefined : selectedDistricts.map(x => x.id as number),
      from: formatDateForReport(from),
      to: formatDateForReport(to),
      isAmrestReport: true,
    };

    runTheoryActualReport(request).then(() => {
      setIsRequestSubmitted(true);
      setSuccessMessage(t('reporting.generic.post-generation-message'));
    }).catch((error) => {
      if (typeof (error) === 'string') {
        setErrorMessage(error);
      }
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {
          allDistricts.length > 0 &&
          <Grid item
            xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                multiple
                limitTags={2}
                options={allDistricts}
                value={selectedDistricts}
                onChange={handleDistrictChange}
                getOptionKey={(option) => option.id ?? ''}
                getOptionLabel={(option) => option.description ?? ''}
                disabled={selectedStores.length > 0 || checkedAllStores}
                renderInput={(params) => (
                  <TextField {...params}
                    required={selectedStores.length === 0 && !checkedAllStores && selectedDistricts.length === 0}
                    label={t('reporting.select-districts')}
                    placeholder={t('reporting.select-districts')} />
                )}
              />
            </FormControl>
          </Grid>
        }
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={user?.stores ?? []}
              value={selectedStores}
              disabled={selectedDistricts.length > 0 || checkedAllStores}
              onChange={handleSelectChange}
              getOptionKey={(option) => option.storeNumber ?? ''}
              getOptionLabel={(option) => option.storeName ?? ''}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedStores.length === 0 && !checkedAllStores && selectedDistricts.length === 0}
                  label={t('reporting.select-stores')}
                  placeholder={t('reporting.select-stores')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox
                checked={checkedAllStores}
                disabled={selectedDistricts.length > 0}
                onChange={handleSelectAllChange} />}
              label={t('reporting.all-stores')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={allCategories}
              value={selectedCategories}
              onChange={handleCategorySelectChange}
              getOptionKey={(option) => option.id ?? ''}
              getOptionLabel={(option) => option.primaryCategoryName ?? ''}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedCategories.length === 0}
                  label={t('reporting.select-categories')}
                  placeholder={t('reporting.select-categories')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox checked={allCategories.length === selectedCategories.length}
                onChange={handleSelectAllCategoriesChange} />}
              label={t('reporting.all-categories')}
            />
          </FormGroup>
        </Grid>

        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              options={availableStartDates.slice(1)} // Exclude most recent date
              value={fromDate || null}
              disabled={selectedStores.length === 0 && !checkedAllStores}
              onChange={handleDateChange}
              getOptionLabel={(option) => option ? getFormattedDate(new Date(option)) : ''}
              renderInput={(params) => (
                <TextField {...params}
                  required
                  label={t('reporting.start-date')}
                  placeholder={t('reporting.start-date')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              options={availableEndDates.filter(date => date > fromDate)}
              value={toDate || null}
              disabled={!fromDate}
              onChange={(event, value) => setToDate(value ?? '')}
              getOptionLabel={(option) => option ? getFormattedDate(new Date(option)) : ''}
              getOptionKey={(option) => option}
              renderInput={(params) => (
                <TextField {...params}
                  required
                  label={t('reporting.end-date')}
                  placeholder={t('reporting.end-date')} />
              )}
            />
          </FormControl>
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage &&
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary'
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ActualVsTheoryAmrestReportForm;